import React from 'react';
import { HelpCircle, Lightbulb, Plus, X, AlertTriangle, Footprints, FileText, Smile } from 'lucide-react';
import { Section, Field, AutoText, ListEditor, Segmented } from './OnsiteFields';
import { uid } from './useOnsitePlan';

const BLOCKS = { 1: 'Block 1 · Unternehmer & Werdegang', 2: 'Block 2 · Geschäft, Kunden, Systeme', 3: 'Block 3 · Zahlen & Organisation', 4: 'Block 4 · Nachfolge & Weg nach vorn' };

export function QuestionsSection({ plan, setField }) {
  const qs = plan.questions || [];
  const save = (v) => setField('questions', v, 'questions');
  const upd = (id, patch) => save(qs.map((q) => (q.id === id ? { ...q, ...patch } : q)));
  return (
    <Section id="os-questions" icon={HelpCircle} title="Fragenbank" subtitle="[X] = Frage kommt von Dritten (Beirat / Investor)" meta={plan.section_meta?.questions} data={qs} testId="onsite-questions">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {[1, 2, 3, 4].map((blk) => (
          <div key={blk} className="border border-border/70 rounded-sm p-3 space-y-2" data-testid={`onsite-questions-block-${blk}`}>
            <p className="text-[10px] uppercase tracking-wider text-teal-400/80">{BLOCKS[blk]}</p>
            {qs.filter((q) => q.block === blk).map((q) => (
              <div key={q.id} className="flex items-start gap-1.5 group">
                <button onClick={() => upd(q.id, { third_party: q.third_party ? '' : 'X' })} title="Frage von Dritten (Beirat/Investor)?"
                  className={`mt-1 text-[9px] font-mono border rounded px-1 py-0.5 shrink-0 ${q.third_party ? 'border-violet-500/50 bg-violet-500/10 text-violet-300' : 'border-border text-muted-foreground/40 hover:text-muted-foreground'}`}>X</button>
                <AutoText value={q.text} rows={1} onChange={(v) => upd(q.id, { text: v })} testId={`onsite-question-${q.id}`} />
                <button onClick={() => save(qs.filter((x) => x.id !== q.id))} className="mt-1.5 text-muted-foreground/30 hover:text-red-400 opacity-0 group-hover:opacity-100"><X className="w-3.5 h-3.5" /></button>
              </div>
            ))}
            <button onClick={() => save([...qs, { id: uid(), block: blk, text: '', third_party: '' }])} data-testid={`onsite-question-add-${blk}`}
              className="inline-flex items-center gap-1 text-[11px] text-teal-400 hover:text-teal-300"><Plus className="w-3 h-3" /> Frage</button>
          </div>
        ))}
      </div>
    </Section>
  );
}

const VERDICTS = [{ value: 'open', label: 'offen' }, { value: 'confirmed', label: 'bestätigt', cls: 'bg-emerald-600 text-white' }, { value: 'refuted', label: 'widerlegt', cls: 'bg-red-700 text-white' }];

export function HypothesesSection({ plan, setField }) {
  const hs = plan.hypotheses || [];
  const save = (v) => setField('hypotheses', v, 'hypotheses');
  const upd = (id, patch) => save(hs.map((h) => (h.id === id ? { ...h, ...patch } : h)));
  return (
    <Section id="os-hypotheses" icon={Lightbulb} iconClass="text-amber-400" title="Hypothesen & Entscheidungsregel" subtitle="vor Ort prüfen — nach dem Termin bestätigen / widerlegen"
      meta={plan.section_meta?.hypotheses} data={{ h: hs, d: plan.decision_rule }} testId="onsite-hypotheses"
      right={<button onClick={() => save([...hs, { id: uid(), text: '', verdict: 'open', note: '' }])} data-testid="onsite-hypothesis-add"
        className="inline-flex items-center gap-1 text-[11px] text-teal-400 hover:text-teal-300 border border-teal-900/40 rounded-sm px-2 py-1"><Plus className="w-3 h-3" /> Hypothese</button>}>
      <div className="space-y-2">
        {hs.map((h, i) => (
          <div key={h.id} className={`border rounded-sm p-3 space-y-2 ${h.verdict === 'confirmed' ? 'border-emerald-500/30 bg-emerald-500/5' : h.verdict === 'refuted' ? 'border-red-500/30 bg-red-500/5' : 'border-border/70'}`} data-testid={`onsite-hypothesis-${i}`}>
            <div className="flex items-start gap-2">
              <span className="text-[10px] font-mono text-muted-foreground mt-2 shrink-0">H{i + 1}</span>
              <AutoText value={h.text} rows={1} onChange={(v) => upd(h.id, { text: v })} testId={`onsite-hypothesis-${i}-text`} />
              <Segmented value={h.verdict} options={VERDICTS} onChange={(v) => upd(h.id, { verdict: v })} testId={`onsite-hypothesis-${i}-verdict`} />
              <button onClick={() => save(hs.filter((x) => x.id !== h.id))} className="mt-2 text-muted-foreground/30 hover:text-red-400"><X className="w-3.5 h-3.5" /></button>
            </div>
            {h.verdict !== 'open' && <AutoText value={h.note} rows={1} onChange={(v) => upd(h.id, { note: v })} placeholder="Was haben wir vor Ort gesehen / gehört?" className="ml-6" />}
          </div>
        ))}
      </div>
      <Field label="Entscheidungsregel"><AutoText value={plan.decision_rule} onChange={(v) => setField('decision_rule', v, 'hypotheses')} testId="onsite-decision-rule" /></Field>
    </Section>
  );
}

export function ListsSection({ plan, setField }) {
  const L = (k, label, Icon, cls) => (
    <div className="space-y-2">
      <p className={`text-[10px] uppercase tracking-wider flex items-center gap-1.5 ${cls}`}><Icon className="w-3 h-3" /> {label}</p>
      <ListEditor items={plan[k] || []} onChange={(v) => setField(k, v, k)} testId={`onsite-${k}`} />
    </div>
  );
  return (
    <Section id="os-lists" icon={AlertTriangle} iconClass="text-red-400" title="Red Flags · Rundgang · Unterlagen · Tonalität"
      meta={plan.section_meta?.red_flags || plan.section_meta?.tour || plan.section_meta?.docs_list} data={{ a: plan.red_flags, b: plan.tour, c: plan.docs_list, d: plan.tone_yes, e: plan.tone_no }} testId="onsite-lists">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {L('red_flags', 'Red Flags — worauf wir achten', AlertTriangle, 'text-red-400/90')}
        {L('tour', 'Rundgang — was wir sehen wollen', Footprints, 'text-teal-400/90')}
        {L('docs_list', 'Unterlagenliste (mit dem Inhaber vereinbaren)', FileText, 'text-sky-400/90')}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {L('tone_yes', 'Tonalität — ja', Smile, 'text-emerald-400/90')}
          {L('tone_no', 'Tonalität — nein', X, 'text-amber-400/90')}
        </div>
      </div>
    </Section>
  );
}
